// client/src/components/Footer.js
import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Grid, Typography, Stack, Divider } from '@mui/material';
import { FeatureFlagContext } from '../context/FeatureFlagContext';
import { useStore } from '../context/StoreContext';
import GavelIcon from '@mui/icons-material/Gavel';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AppleIcon from '@mui/icons-material/Apple';
import GoogleIcon from '@mui/icons-material/Google';
import PaymentIcon from '@mui/icons-material/Payment';

const FooterLink = ({ to, children }) => (
  <Typography
    component={Link}
    to={to}
    variant="body2"
    sx={{
      color: 'rgba(255,255,255,0.65)',
      textDecoration: 'none',
      transition: 'color 0.2s ease, transform 0.2s ease',
      display: 'inline-block',
      '&:hover': { color: '#fff', transform: 'translateX(3px)' },
    }}
  >
    {children}
  </Typography>
);

const ColumnHeading = ({ children }) => (
  <Typography
    variant="overline"
    sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 700, letterSpacing: '0.12em', display: 'block', mb: 1.5 }}
  >
    {children}
  </Typography>
);

const Footer = () => {
  const { isEnabled } = useContext(FeatureFlagContext);
  const { store } = useStore();
  return (
    <Box
      component="footer"
      className="no-print"
      sx={{
        mt: 10,
        bgcolor: '#0B0D12',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: (theme) =>
            `radial-gradient(900px 300px at 0% 0%, ${theme.palette.primary.main}33, transparent 60%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="xl" sx={{ position: 'relative', py: { xs: 6, md: 8 } }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={5}>
            <Typography variant="h5" fontWeight={800} letterSpacing="-0.02em" sx={{ color: '#fff', mb: 1.5 }}>
              {store.name}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', maxWidth: 380, mb: 3 }}>
              Everyday shopping and live auctions, all in one place.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'rgba(255,255,255,0.55)' }}>
              <LockOutlinedIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption">256-bit SSL secure checkout</Typography>
            </Stack>
            <Stack direction="row" spacing={1.25} sx={{ mt: 2, color: 'rgba(255,255,255,0.45)' }}>
              <CreditCardIcon fontSize="small" titleAccess="Card" />
              <AppleIcon fontSize="small" titleAccess="Apple Pay" />
              <GoogleIcon fontSize="small" titleAccess="Google Pay" />
              <PaymentIcon fontSize="small" titleAccess="Klarna, Afterpay, Clearpay" />
            </Stack>
          </Grid>
          <Grid item xs={6} md={3}>
            <ColumnHeading>Shop</ColumnHeading>
            <Stack spacing={1.25}>
              <FooterLink to="/">Home</FooterLink>
              {isEnabled('auctions') && <FooterLink to="/auctions">Live Auctions</FooterLink>}
              <FooterLink to="/cart">Cart</FooterLink>
            </Stack>
          </Grid>
          <Grid item xs={6} md={4}>
            <ColumnHeading>Account &amp; Support</ColumnHeading>
            <Stack spacing={1.25}>
              <FooterLink to="/profile">My Account</FooterLink>
              {isEnabled('become_seller') && <FooterLink to="/become-seller">Sell on {store.name}</FooterLink>}
              <FooterLink to="/returns">Returns &amp; Refunds</FooterLink>
              <FooterLink to="/login">Login</FooterLink>
            </Stack>
          </Grid>
        </Grid>
        <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.08)' }} />
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
            © {new Date().getFullYear()} {store.name}. All rights reserved.
          </Typography>
          {isEnabled('auctions') && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'rgba(255,255,255,0.45)' }}>
              <GavelIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption">Live auctions running now</Typography>
            </Stack>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default Footer;
